import {makeAutoObservable, observable} from 'mobx';
import StudioRepository from '../../../../../data/repositories/StudioRepository';
import {
  defaultMotionChoices,
  type MotionChoices,
  readMotionDesign,
} from '../../../motionDesign';
import {studioRequest} from '../../../store/studioRequests';
import type {ImageStyle, ReelMode, VideoMode} from '../../../types';
import {
  CreateForm,
  DEFAULT_FORM,
  DEFAULT_IMAGE_STYLE,
  DEFAULT_OPTIONS,
  LIBRARY_MUSIC,
  LibraryFile,
  mediaSourceFor,
  StudioOptions,
  BrowseAudio,
  titleFromFileName,
  type VideoType,
} from '../createOptions';
import CourseCategoriesStore from './CourseCategoriesStore';
import NarrationStore from './NarrationStore';
import SourceStore from './SourceStore';
import SubmissionStore from './SubmissionStore';
import type {ListVoices} from './LibraryVoicesStore';
import * as choices from './formChoices';

type PrivateFields = 'repository' | 'session';

/**
 * The create page's form: the server's choices and the course and microreel settings. The
 * source (`source`), the narration (`narration`), the course categories (`categories`) and
 * creating the job (`submission`) keep their own stores; what the form offers follows from
 * `formChoices`.
 *
 * The page opens the store as it mounts and closes it as it unmounts: answers that arrive
 * after it closed (the options, a library pick, an upload, the new job) are dropped.
 */
class CreateStore {
  private repository = new StudioRepository();
  private session: object | null = null;

  /** The organization the page creates in; Studio is only routed inside one. */
  public organizationId: string | undefined = undefined;
  public readonly categories = new CourseCategoriesStore();
  public readonly source: SourceStore = new SourceStore(this);
  public readonly narration: NarrationStore = new NarrationStore(this);
  public readonly submission: SubmissionStore = new SubmissionStore(this);
  public options: StudioOptions = DEFAULT_OPTIONS;
  // The fallback choices omit videos. Wait for the server before the author can open
  // a library whose available tabs are fixed for that visit.
  public optionsLoading = true;
  /** Studio is missing from this environment: nothing can be created. */
  public catalogError: string | null = null;
  public musicFile: LibraryFile | null = null;
  public reelMode: ReelMode = 'single';
  // Video choices and the image style are kept when switching sources or visual modes.
  public videoMode: VideoMode = 'clipping';
  public imageStyle: ImageStyle = DEFAULT_IMAGE_STYLE;
  // The title follows the chosen document until the author types their own.
  public titleEdited = false;
  public customize = false;
  /** The motion and sound settings are open. */
  public motionOpen = false;
  /** The motion choices, from the server's defaults; null while it offers none. */
  public motion: MotionChoices | null = null;
  public form: CreateForm = DEFAULT_FORM;

  constructor() {
    makeAutoObservable<CreateStore, PrivateFields>(
      this,
      {
        repository: false,
        session: false,
        categories: false,
        source: false,
        narration: false,
        submission: false,
        options: observable.ref,
        musicFile: observable.ref,
        motion: observable.ref,
        form: observable.ref,
      },
      {autoBind: true},
    );
  }

  // ---- What the form shows (see formChoices) ----

  public get caps() {
    return choices.capabilities(this);
  }

  public get videos(): boolean {
    return choices.videosAllowed(this);
  }

  public get maxDocumentMb() {
    return choices.maxDocumentMb(this);
  }

  public get canClip() {
    return choices.canClip(this);
  }

  public get clipping() {
    return choices.clipping(this);
  }

  public get perTopic() {
    return choices.perTopic(this);
  }

  public get visualChoices() {
    return choices.visualChoices(this);
  }

  public get visuals() {
    return choices.visuals(this);
  }

  public get videoTypeChoices() {
    return choices.videoTypeChoices(this);
  }

  public get showVideoType() {
    return choices.showVideoType(this);
  }

  /** Still images or stock footage: what the brief's media source asks for. */
  public get videoType() {
    return choices.videoType(this);
  }

  public get showImageStyle() {
    return choices.showImageStyle(this);
  }

  /** The motion choices the server offers, or null. */
  public get motionDesign() {
    return this.options.motionDesign ?? null;
  }

  public get showMotion() {
    return choices.showMotion(this);
  }

  /** A job is being created. */
  public get busy() {
    return this.submission.busy;
  }

  /** Why the form can't go ahead, or why creating the job failed. */
  public get error() {
    return this.submission.error;
  }

  /** Creating the job was refused for too few AI credits. */
  public get needsCredits() {
    return this.submission.needsCredits;
  }

  public get locked() {
    return this.optionsLoading || this.busy || !!this.catalogError;
  }

  // ---- Opening and closing the page ----

  /** Starts a fresh form for the page, and reads the server's choices and the categories. */
  public open(organizationId?: string) {
    this.reset();
    const session = {};
    this.session = session;
    this.organizationId = organizationId;
    this.categories.load(organizationId);
    studioRequest<Partial<StudioOptions>>(organizationId, org =>
      this.repository.options<Partial<StudioOptions>>(org),
    ).then(
      loaded => {
        if (this.session === session) this.applyOptions(loaded ?? {});
      },
      error => {
        if (this.session === session) this.catalogFailed(error);
      },
    );
  }

  /** Leaving the page drops every answer still to come and stops an uploading video. */
  public close() {
    this.session = null;
    this.reset();
  }

  /** A check that stays true while the page stays open as it is now; null when it is closed. */
  public sessionCheck(): (() => boolean) | null {
    const session = this.session;
    return session ? () => this.session === session : null;
  }

  public applyOptions(loaded: Partial<StudioOptions>) {
    this.optionsLoading = false;
    const motionDesign = readMotionDesign(loaded.motionDesign);
    this.options = {
      ...DEFAULT_OPTIONS,
      ...loaded,
      imageStyles: loaded.imageStyles ?? [],
      motionDesign,
    };
    this.motion = motionDesign ? defaultMotionChoices(motionDesign) : null;
    this.patch({
      mediaSource: loaded.capabilities?.storyblocks
        ? this.form.mediaSource
        : 'ai',
    });
  }

  /** A missing catalog (404) or an unconfigured Studio (503) means nothing can be created here. */
  public catalogFailed(error: unknown) {
    this.optionsLoading = false;
    const status = (error as {status?: number} | null)?.status;
    if (status === 404 || status === 503)
      this.catalogError = (error as Error).message;
  }

  // ---- The settings ----

  public setTitle(title: string) {
    this.patch({title});
    this.titleEdited = title !== '';
  }

  /** A chosen file names the microreel until the author types a title. */
  public fileChosen(fileName: string) {
    if (!this.titleEdited) this.patch({title: titleFromFileName(fileName)});
  }

  public setPrompt(prompt: string) {
    this.patch({prompt});
  }

  public setPublic(isPublic: boolean) {
    // The form is locked while a job starts.
    if (!this.locked) this.patch({is_public: isPublic});
  }

  public setReelMode(reelMode: ReelMode) {
    this.reelMode = reelMode;
  }

  public setVideoMode(videoMode: VideoMode) {
    this.videoMode = videoMode;
  }

  public setLengthBand(lengthBand: string) {
    this.patch({lengthBand});
  }

  public setEnding(ending: string) {
    this.patch({ending});
  }

  /** The library entry opens the library; cancelling it keeps the previous choice. */
  public async chooseMusic(option: string, browse?: BrowseAudio) {
    if (option !== LIBRARY_MUSIC) {
      this.musicFile = null;
      this.patch({backgroundMusic: option});
      return;
    }
    const session = this.session;
    const selected = await browse?.();
    if (selected && this.session === session) this.setLibraryMusic(selected);
  }

  public setLibraryMusic(file: LibraryFile) {
    this.musicFile = file;
    this.patch({backgroundMusic: LIBRARY_MUSIC});
  }

  public setCustomize(open: boolean) {
    this.customize = open;
  }

  public setVisuals(visuals: string) {
    this.patch({visuals});
  }

  public setVideoType(videoType: VideoType) {
    this.patch({mediaSource: mediaSourceFor(videoType)});
  }

  public setImageStyle(imageStyle: string) {
    this.imageStyle = imageStyle as ImageStyle;
  }

  public setMotionOpen(open: boolean) {
    this.motionOpen = open;
  }

  /** Changes motion choices; nothing while the server offers none, or the form is locked. */
  public setMotion(change: Partial<MotionChoices>) {
    if (this.motion && !this.locked) this.motion = {...this.motion, ...change};
  }

  public patch(change: Partial<CreateForm>) {
    this.form = {...this.form, ...change};
  }

  /** Shows why the form can't go ahead, or clears the message with null. */
  public report(problem: string | null): null {
    return this.submission.report(problem);
  }

  /** The voices to choose from, read for this page's organization. */
  public listVoices(...[query, signal]: Parameters<ListVoices>) {
    return studioRequest(this.organizationId, org =>
      this.repository.voices(org, query, {signal}),
    );
  }

  /** Creates the job from the form (see SubmissionStore). */
  public submit() {
    return this.submission.submit();
  }

  // ---- Helpers ----

  private reset() {
    this.submission.reset();
    this.organizationId = undefined;
    this.categories.reset();
    this.source.reset();
    this.narration.reset();
    this.options = DEFAULT_OPTIONS;
    this.optionsLoading = true;
    this.catalogError = null;
    this.musicFile = null;
    this.reelMode = 'single';
    this.videoMode = 'clipping';
    this.imageStyle = DEFAULT_IMAGE_STYLE;
    this.titleEdited = false;
    this.customize = false;
    this.motionOpen = false;
    this.motion = null;
    this.form = DEFAULT_FORM;
  }
}

export default CreateStore;
